import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, ArrowLeft, X, Upload, Trash2 } from 'lucide-react';
import api from '../../services/api';

const TIPOS = [
  ['apartamento', 'Apartamento'], ['cobertura', 'Cobertura'], ['studio', 'Studio'],
  ['duplex', 'Duplex'], ['triplex', 'Triplex'], ['casa', 'Casa'],
  ['casa_condominio', 'Casa em Condomínio'], ['sobrado', 'Sobrado'],
  ['terreno', 'Terreno'], ['lote', 'Lote'], ['sala_comercial', 'Sala Comercial'],
  ['loja', 'Loja'], ['galpao', 'Galpão'], ['flat', 'Flat'], ['kitnet', 'Kitnet'],
];

const POSICAO_SOLAR = ['Norte', 'Sul', 'Leste', 'Oeste', 'Norte/Sul', 'Leste/Oeste', 'Nordeste', 'Sudeste'];

const ABAS = ['Dados Básicos', 'Localização', 'Características', 'Valores', 'Mídia'];

const EMPTY = {
  titulo: '', descricao: '', descricao_curta: '', tipo_imovel: 'apartamento',
  finalidade: 'venda', empreendimento_id: '', unidade_id: '', responsavel_id: '',
  endereco: '', numero: '', complemento: '', bairro: '', cidade: 'Maringá', estado: 'PR', cep: '', andar: '', bloco: '',
  area_privativa: '', area_total: '', area_terreno: '',
  num_dormitorios: 0, num_suites: 0, num_banheiros: 0, num_vagas: 0,
  preco_venda: '', preco_locacao: '', condominio: '', iptu: '',
  aceita_financiamento: true, aceita_fgts: false,
  posicao_solar: '', possui_churrasqueira: false, possui_piscina: false,
  possui_varanda: false, possui_area_servico: false,
  diferenciais: [], destaque: false, exclusivo: false, video_url: '', tour_virtual_url: '',
  status_anuncio: 'ativo',
};

function Campo({ label, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, type = 'text', placeholder, className = '' }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary ${className}`}
    />
  );
}

function Select({ value, onChange, children }) {
  return (
    <select
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
    >
      {children}
    </select>
  );
}

function NumInput({ value, onChange, min = 0 }) {
  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={() => onChange(Math.max(min, Number(value) - 1))} className="w-8 h-8 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center justify-center font-bold">−</button>
      <span className="w-8 text-center font-medium">{value ?? 0}</span>
      <button type="button" onClick={() => onChange(Number(value) + 1)} className="w-8 h-8 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center justify-center font-bold">+</button>
    </div>
  );
}

export default function ImovelForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isEdit = !!id;
  const [aba, setAba] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [diferencialInput, setDiferencialInput] = useState('');
  const [fotosPreview, setFotosPreview] = useState([]);
  const [fotosExistentes, setFotosExistentes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const { data: empreendimentos } = useQuery({
    queryKey: ['empreendimentos'],
    queryFn: () => api.get('/empreendimentos').then(r => r.data.data),
  });

  const { data: unidades } = useQuery({
    queryKey: ['unidades-emp', form.empreendimento_id],
    queryFn: () => api.get('/unidades', { params: { empreendimento_id: form.empreendimento_id } }).then(r => r.data.data),
    enabled: !!form.empreendimento_id,
  });

  const { data: usuarios } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api.get('/auth/usuarios').then(r => r.data.data).catch(() => []),
  });

  const { data: imovelData } = useQuery({
    queryKey: ['imovel', id],
    queryFn: () => api.get(`/imoveis/${id}`).then(r => r.data.data),
    enabled: isEdit,
  });

  useEffect(() => {
    if (imovelData) {
      const { imoveis_fotos, portais, ...rest } = imovelData;
      setForm({ ...EMPTY, ...rest });
      setFotosExistentes(imoveis_fotos || []);
    }
  }, [imovelData]);

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  // Buscar CEP
  async function buscarCep(cep) {
    const n = cep.replace(/\D/g, '');
    if (n.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${n}/json/`);
      const d = await r.json();
      if (!d.erro) setForm(f => ({ ...f, endereco: d.logradouro || f.endereco, bairro: d.bairro || f.bairro, cidade: d.localidade || f.cidade, estado: d.uf || f.estado }));
    } catch (_) {}
  }

  function adicionarDiferencial(e) {
    if (e.key === 'Enter' && diferencialInput.trim()) {
      e.preventDefault();
      const v = diferencialInput.trim();
      if (!form.diferenciais.includes(v)) setForm(f => ({ ...f, diferenciais: [...f.diferenciais, v] }));
      setDiferencialInput('');
    }
  }

  function removerDiferencial(d) {
    setForm(f => ({ ...f, diferenciais: f.diferenciais.filter(x => x !== d) }));
  }

  const mutSave = useMutation({
    mutationFn: (dados) => isEdit
      ? api.put(`/imoveis/${id}`, dados)
      : api.post('/imoveis', dados),
    onSuccess: async (res) => {
      const novoId = id || res.data.data?.id;
      // Upload de fotos novas
      if (fotosPreview.length > 0 && novoId) {
        setUploading(true);
        const fd = new FormData();
        fotosPreview.forEach(f => fd.append('fotos', f.file));
        await api.post(`/imoveis/${novoId}/fotos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setUploading(false);
      }
      qc.invalidateQueries(['imoveis']);
      navigate(`/imoveis/${novoId}`);
    },
  });

  async function removerFotoExistente(fotoId) {
    await api.delete(`/imoveis/${id}/fotos/${fotoId}`);
    setFotosExistentes(f => f.filter(x => x.id !== fotoId));
  }

  function onFileChange(e) {
    const novos = Array.from(e.target.files).map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setFotosPreview(p => [...p, ...novos]);
  }

  function handleSubmit(e) {
    e.preventDefault();
    const dados = { ...form };
    // Limpar campos vazios opcionais
    ['unidade_id', 'responsavel_id', 'andar', 'area_privativa', 'area_total', 'area_terreno',
      'preco_venda', 'preco_locacao', 'condominio', 'iptu'].forEach(k => {
      if (dados[k] === '' || dados[k] === null) dados[k] = undefined;
    });
    mutSave.mutate(dados);
  }

  const totalFotos = fotosExistentes.length + fotosPreview.length;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(isEdit ? `/imoveis/${id}` : '/imoveis')} className="text-gray-400 hover:text-gray-700">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">{isEdit ? 'Editar Imóvel' : 'Novo Imóvel'}</h1>
      </div>

      {/* Abas de navegação */}
      <div className="flex border-b border-gray-200">
        {ABAS.map((a, i) => (
          <button
            key={a}
            type="button"
            onClick={() => setAba(i)}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${aba === i ? 'border-secondary text-secondary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {a}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-xl shadow p-6 space-y-5">

          {/* ABA 1 — Dados Básicos */}
          {aba === 0 && (
            <>
              <Campo label="Título *">
                <Input value={form.titulo} onChange={set('titulo')} placeholder="Ex: Apartamento 3 dorm, 2 suítes no Edifício Aurora" />
              </Campo>
              <div className="grid grid-cols-2 gap-4">
                <Campo label="Tipo do Imóvel *">
                  <Select value={form.tipo_imovel} onChange={set('tipo_imovel')}>
                    {TIPOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </Select>
                </Campo>
                <Campo label="Finalidade">
                  <Select value={form.finalidade} onChange={set('finalidade')}>
                    <option value="venda">Venda</option>
                    <option value="locacao">Locação</option>
                    <option value="venda_locacao">Venda e Locação</option>
                  </Select>
                </Campo>
              </div>
              <Campo label="Empreendimento *">
                <Select value={form.empreendimento_id} onChange={set('empreendimento_id')}>
                  <option value="">Selecione...</option>
                  {(empreendimentos || []).map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                </Select>
              </Campo>
              <div className="grid grid-cols-2 gap-4">
                <Campo label="Vínculo com Unidade (opcional)">
                  <Select value={form.unidade_id} onChange={set('unidade_id')}>
                    <option value="">Nenhuma</option>
                    {(unidades || []).map(u => <option key={u.id} value={u.id}>{u.identificador}{u.torre ? ` - Torre ${u.torre}` : ''}</option>)}
                  </Select>
                </Campo>
                <Campo label="Responsável">
                  <Select value={form.responsavel_id} onChange={set('responsavel_id')}>
                    <option value="">Não atribuído</option>
                    {(usuarios || []).map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                  </Select>
                </Campo>
              </div>
              <Campo label="Descrição completa">
                <textarea
                  value={form.descricao || ''}
                  onChange={e => set('descricao')(e.target.value)}
                  rows={6}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                  placeholder="Descrição detalhada do imóvel..."
                />
              </Campo>
              <Campo label="Descrição curta" hint="Máx. 500 caracteres — usada nos portais">
                <textarea
                  value={form.descricao_curta || ''}
                  onChange={e => set('descricao_curta')(e.target.value)}
                  rows={3}
                  maxLength={500}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                  placeholder="Resumo para portais imobiliários..."
                />
                <p className="text-xs text-gray-400 mt-1">{(form.descricao_curta || '').length}/500</p>
              </Campo>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={form.destaque} onChange={e => set('destaque')(e.target.checked)} className="rounded" />
                  Imóvel em Destaque
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={form.exclusivo} onChange={e => set('exclusivo')(e.target.checked)} className="rounded" />
                  Exclusividade
                </label>
              </div>
            </>
          )}

          {/* ABA 2 — Localização */}
          {aba === 1 && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Campo label="CEP">
                    <Input
                      value={form.cep}
                      onChange={v => { set('cep')(v); if (v.replace(/\D/g, '').length === 8) buscarCep(v); }}
                      placeholder="00000-000"
                    />
                  </Campo>
                </div>
                <Campo label="Estado">
                  <Input value={form.estado} onChange={set('estado')} placeholder="PR" />
                </Campo>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Campo label="Endereço">
                    <Input value={form.endereco} onChange={set('endereco')} placeholder="Rua, Avenida..." />
                  </Campo>
                </div>
                <Campo label="Número">
                  <Input value={form.numero} onChange={set('numero')} placeholder="123" />
                </Campo>
              </div>
              <Campo label="Complemento">
                <Input value={form.complemento} onChange={set('complemento')} placeholder="Ap, Sala, etc." />
              </Campo>
              <div className="grid grid-cols-2 gap-4">
                <Campo label="Bairro">
                  <Input value={form.bairro} onChange={set('bairro')} placeholder="Bairro" />
                </Campo>
                <Campo label="Cidade">
                  <Input value={form.cidade} onChange={set('cidade')} placeholder="Maringá" />
                </Campo>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Campo label="Andar">
                  <Input type="number" value={form.andar} onChange={set('andar')} placeholder="Ex: 12" />
                </Campo>
                <Campo label="Bloco / Torre">
                  <Input value={form.bloco} onChange={set('bloco')} placeholder="Ex: Torre A" />
                </Campo>
              </div>
            </>
          )}

          {/* ABA 3 — Características */}
          {aba === 2 && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <Campo label="Área Privativa (m²)">
                  <Input type="number" value={form.area_privativa} onChange={set('area_privativa')} placeholder="0" />
                </Campo>
                <Campo label="Área Total (m²)">
                  <Input type="number" value={form.area_total} onChange={set('area_total')} placeholder="0" />
                </Campo>
                <Campo label="Área Terreno (m²)">
                  <Input type="number" value={form.area_terreno} onChange={set('area_terreno')} placeholder="0" />
                </Campo>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  ['num_dormitorios', 'Dormitórios'],
                  ['num_suites', 'Suítes'],
                  ['num_banheiros', 'Banheiros'],
                  ['num_vagas', 'Vagas'],
                ].map(([k, l]) => (
                  <Campo key={k} label={l}>
                    <NumInput value={form[k]} onChange={set(k)} />
                  </Campo>
                ))}
              </div>
              <Campo label="Posição Solar">
                <Select value={form.posicao_solar} onChange={set('posicao_solar')}>
                  <option value="">Não informado</option>
                  {POSICAO_SOLAR.map(p => <option key={p} value={p}>{p}</option>)}
                </Select>
              </Campo>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ['possui_churrasqueira', 'Churrasqueira'],
                  ['possui_piscina', 'Piscina'],
                  ['possui_varanda', 'Varanda/Sacada'],
                  ['possui_area_servico', 'Área de serviço'],
                ].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer bg-gray-50 rounded-lg p-3">
                    <input type="checkbox" checked={form[k]} onChange={e => set(k)(e.target.checked)} className="rounded" />
                    {l}
                  </label>
                ))}
              </div>
              <Campo label="Diferenciais" hint="Digite e pressione Enter para adicionar">
                <div className="flex flex-wrap gap-2 min-h-[2.5rem] border border-gray-200 rounded-lg p-2">
                  {form.diferenciais.map(d => (
                    <span key={d} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                      {d}
                      <button type="button" onClick={() => removerDiferencial(d)}><X size={10} /></button>
                    </span>
                  ))}
                  <input
                    value={diferencialInput}
                    onChange={e => setDiferencialInput(e.target.value)}
                    onKeyDown={adicionarDiferencial}
                    placeholder={form.diferenciais.length === 0 ? 'Ex: Armários planejados, Vista panorâmica...' : ''}
                    className="flex-1 min-w-32 outline-none text-sm bg-transparent"
                  />
                </div>
              </Campo>
            </>
          )}

          {/* ABA 4 — Valores */}
          {aba === 3 && (
            <>
              {(form.finalidade === 'venda' || form.finalidade === 'venda_locacao') && (
                <Campo label="Preço de Venda (R$)">
                  <Input type="number" value={form.preco_venda} onChange={set('preco_venda')} placeholder="Ex: 450000" />
                </Campo>
              )}
              {(form.finalidade === 'locacao' || form.finalidade === 'venda_locacao') && (
                <Campo label="Preço de Locação (R$)">
                  <Input type="number" value={form.preco_locacao} onChange={set('preco_locacao')} placeholder="Ex: 2500" />
                </Campo>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Campo label="Condomínio Mensal (R$)">
                  <Input type="number" value={form.condominio} onChange={set('condominio')} placeholder="Ex: 600" />
                </Campo>
                <Campo label="IPTU Anual (R$)">
                  <Input type="number" value={form.iptu} onChange={set('iptu')} placeholder="Ex: 1200" />
                </Campo>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={form.aceita_financiamento} onChange={e => set('aceita_financiamento')(e.target.checked)} className="rounded" />
                  Aceita Financiamento
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={form.aceita_fgts} onChange={e => set('aceita_fgts')(e.target.checked)} className="rounded" />
                  Aceita FGTS
                </label>
              </div>
            </>
          )}

          {/* ABA 5 — Mídia */}
          {aba === 4 && (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">Fotos ({totalFotos}/30)</p>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={totalFotos >= 30}
                    className="flex items-center gap-2 px-3 py-1.5 border border-secondary text-secondary rounded-lg text-xs hover:bg-secondary hover:text-white disabled:opacity-40"
                  >
                    <Upload size={14} /> Adicionar fotos
                  </button>
                  <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={onFileChange} />
                </div>

                {/* Grade de fotos existentes */}
                {fotosExistentes.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {fotosExistentes.map(f => (
                      <div key={f.id} className="relative group">
                        <img src={f.url_thumbnail || f.url} alt="" className="w-full aspect-video object-cover rounded-lg" />
                        {f.destaque && <span className="absolute top-1 left-1 text-yellow-400 text-xs">⭐</span>}
                        {isEdit && (
                          <button
                            type="button"
                            onClick={() => removerFotoExistente(f.id)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={10} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Prévia de novas fotos */}
                {fotosPreview.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 mb-2">Novas fotos (serão enviadas ao salvar):</p>
                    <div className="grid grid-cols-4 gap-2">
                      {fotosPreview.map((f, i) => (
                        <div key={i} className="relative group">
                          <img src={f.preview} alt="" className="w-full aspect-video object-cover rounded-lg opacity-80" />
                          <button
                            type="button"
                            onClick={() => setFotosPreview(p => p.filter((_, j) => j !== i))}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {totalFotos === 0 && (
                  <div
                    onClick={() => fileRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center cursor-pointer hover:border-secondary transition-colors"
                  >
                    <Upload size={32} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-400">Arraste fotos ou clique para selecionar</p>
                    <p className="text-xs text-gray-300 mt-1">JPG, PNG, WEBP · Máx. 15MB por foto</p>
                  </div>
                )}
              </div>

              <Campo label="URL do Vídeo (YouTube / Vimeo)">
                <Input value={form.video_url} onChange={set('video_url')} placeholder="https://www.youtube.com/watch?v=..." />
              </Campo>
              <Campo label="URL do Tour Virtual">
                <Input value={form.tour_virtual_url} onChange={set('tour_virtual_url')} placeholder="https://meusite.com/tour/..." />
              </Campo>
            </>
          )}
        </div>

        {/* Navegação entre abas + Salvar */}
        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={() => setAba(a => Math.max(0, a - 1))}
            disabled={aba === 0}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            ← Anterior
          </button>
          <div className="flex gap-3">
            {aba < ABAS.length - 1 ? (
              <button
                type="button"
                onClick={() => setAba(a => Math.min(ABAS.length - 1, a + 1))}
                className="px-4 py-2 bg-secondary text-white rounded-lg text-sm hover:opacity-90"
              >
                Próximo →
              </button>
            ) : null}
            <button
              type="submit"
              disabled={mutSave.isPending || uploading}
              className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg text-sm hover:bg-secondary disabled:opacity-50"
            >
              <Save size={16} />
              {mutSave.isPending || uploading ? 'Salvando...' : 'Salvar Imóvel'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
